const IFRAME_MESSAGE_PREFIX = '[iFrameSizer]message:';
export const CALCULATOR_IFRAME_ID = 'cc-calculator';

const WINDOW_METRICS = [
  'innerWidth', 'innerHeight', 'outerWidth', 'outerHeight',
  'pageYOffset', 'pageXOffset', 'scrollX', 'scrollY'
] as const;

/** Mirror widget.min.js onInit payload so calculator UI receives hideUserPlatform, token, etc. */
export const postCalculatorWidgetInitialize = (
  iframe: HTMLIFrameElement,
  containerId: string,
  options: Record<string, unknown>
): void => {
  const win = iframe.contentWindow;
  if (!win) {
    return;
  }
  const container = document.getElementById(containerId);
  const widgetOptions = { ...options };
  delete widgetOptions.callbacks;
  delete widgetOptions.iframeResizeOptions;
  delete widgetOptions.container_id;

  const payload = {
    type: 'widget.initialize',
    connect_to_api: options.user_platform,
    cta: options.cta,
    banner_style: options.banner_style,
    banner_url: options.banner_url,
    options: widgetOptions,
    window: WINDOW_METRICS.reduce<Record<string, number>>((metrics, key) => {
      metrics[key] = window[key];
      return metrics;
    }, {}),
    container: container ? { offsetTop: container.offsetTop } : {}
  };
  win.postMessage(JSON.stringify(payload), '*');
};

export const scheduleCalculatorWidgetInitialize = (
  containerId: string,
  options: Record<string, unknown>
): (() => void) => {
  let attempts = 0;
  // Keep re-posting briefly so the iframe SPA receives hideUserPlatform after its
  // widget.initialize listener is ready (a single early post is often missed).
  const maxAttempts = 25;
  const timer = window.setInterval(() => {
    attempts += 1;
    const iframe = document.getElementById(CALCULATOR_IFRAME_ID) as HTMLIFrameElement | null;
    if (iframe && iframe.contentWindow) {
      postCalculatorWidgetInitialize(iframe, containerId, options);
    }
    if (attempts >= maxAttempts) {
      window.clearInterval(timer);
    }
  }, 200);
  return () => window.clearInterval(timer);
};

/** Ask the embedded Next calculator to navigate (e.g. `/results/summary`). */
export const postCalculatorNavigate = (path: string): void => {
  const iframe = document.getElementById(CALCULATOR_IFRAME_ID) as HTMLIFrameElement | null;
  const win = iframe && iframe.contentWindow;
  if (!win) {
    return;
  }
  win.postMessage(
    JSON.stringify({
      type: 'widget.navigate',
      path
    }),
    '*'
  );
};

/** Tear down iframe-resizer before removing the calculator DOM (avoids focus-handler spam). */
export const destroyCalculatorWidget = (containerId: string): void => {
  const iframe = document.getElementById(CALCULATOR_IFRAME_ID) as HTMLIFrameElement | null;
  const resizer = iframe && (iframe as HTMLIFrameElement & {
    iFrameResizer?: { close?: () => void };
  }).iFrameResizer;
  if (resizer && typeof resizer.close === 'function') {
    try {
      resizer.close();
    } catch (_err) {
      // Widget may already be mid-teardown.
    }
  }
  const container = document.getElementById(containerId);
  if (container) {
    container.innerHTML = '';
  }
};

export const parseWindowPostMessage = (event: MessageEvent): CalculatorIframeMessage | undefined => {
  const data = event.data;
  if (typeof data !== 'string' || data.indexOf(IFRAME_MESSAGE_PREFIX) !== 0) {
    return undefined;
  }
  try {
    return JSON.parse(data.slice(IFRAME_MESSAGE_PREFIX.length)) as CalculatorIframeMessage;
  } catch (err) {
    return undefined;
  }
};

export const attachCalculatorWindowMessageListener = (
  handler: (message: CalculatorIframeMessage) => void
): (() => void) => {
  const listener = (event: MessageEvent) => {
    const message = parseWindowPostMessage(event);
    if (message) {
      handler(message);
    }
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};

export interface CalculatorIframeMessage {
  type: string;
  data?: Record<string, unknown>;
}

export interface CalculatorIframeMessagePayload {
  iframe?: HTMLIFrameElement;
  message?: CalculatorIframeMessage | string;
}

const CALCULATOR_TAKE_ACTION_ROUTE = 'take_action';

export const parseCalculatorIframeMessage = (
  payload: CalculatorIframeMessagePayload
): CalculatorIframeMessage | undefined => {
  const raw = payload && payload.message;
  if (!raw) {
    return undefined;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as CalculatorIframeMessage;
    } catch (err) {
      return undefined;
    }
  }
  if (typeof raw === 'object' && raw.type) {
    return raw as CalculatorIframeMessage;
  }
  return undefined;
};

export const calculatorCompletionReached = (data?: Record<string, unknown>): boolean => {
  if (!data) {
    return false;
  }
  if (data.allPagesVisited) {
    return true;
  }
  const visited = data.visited;
  return Array.isArray(visited) && visited.indexOf(CALCULATOR_TAKE_ACTION_ROUTE) >= 0;
};

export const isCalculatorTakeActionMessage = (message?: CalculatorIframeMessage): boolean => {
  if (!message) {
    return false;
  }
  if (message.type === 'onTakeActionVisited') {
    return true;
  }
  // Only treat full completion as a trigger; visited[] is cumulative and may
  // include take_action before the user is actually on that page (e.g. restored iframe state).
  if (message.type === 'page:visited' && message.data && message.data.allPagesVisited) {
    return true;
  }
  return false;
};
